<script setup>
/**
 * FormControlModulestructureOptions
 * Sub-orchestration layout bridge for select menu option datasets.
 * Dynamically updates field property constraints and tracks component type alterations,
 * utilizing strict visual concealment patterns rather than component teardowns.
 */
import {computed, watch, onMounted} from 'vue'
import {useFormStore} from '@/stores/form'
import FormSelectOptions from '@/common/form/FormSelectOptions.vue'

const props = defineProps({
    // Standard Orchestrator Props
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    }
})

// Dynamic Pinia Store
const formStore = computed(() => props.formId ? useFormStore(props.formId) : null)

// --- Computed ---

/**
 * References the active data container from the current form module.
 */
const currentData = computed(() => formStore.value?.currentData || {})

/**
 * Isolates the active field type state configuration.
 */
const currentType = computed(() => currentData.value?.type || '')

/**
 * References active database table configurations out of the current form session.
 */
const tableName = computed(() => formStore.value?.tableName)

// --- Methods ---

/**
 * Initialization routine configuring layout properties contextually.
 * Swaps between empty display strings and 'hidden' class flags to keep the component
 * mounted and responsive inside the broader form lifecycle.
 */
const init = () => {
    if (!props.field || !currentData.value) return

    props.field.visibility = ''

    switch (currentType.value) {
        case 'select':
            // Overwrite label configuration cleanly
            props.field.label = 'Options'

            // The following was used when the JSON was presented in a textarea.
            // It might be worth retaining this for reference.
            // Use stringify's built in prettify.
            // ctrl.form.currentData.options = JSON.stringify(ctrl.form.currentData.options, undefined, 2);
            // ctrl.form.presetData(ctrl.field.name, ctrl.form.currentData.options);
            break

        default:
            // CRITICAL: field.hidden cannot be used here because it removes this component completely,
            // and so its watch and computed don't operate.
            props.field.visibility = 'hidden'
            break
    }
}

// --- Watchers & Lifecycle ---

/**
 * Watcher: Triggers initialization routines whenever the structural
 * component layout type switches, ensuring configuration sync.
 */
watch(currentType, () => {
    init()
})

onMounted(() => {
    init()
})
</script>

<template>
    <FormSelectOptions
        v-if="currentData?.type === 'select'"
        :current-data="currentData"
        :field="field"
        :form-id="formId"
    />
</template>

<style scoped>
/* Underlying options configuration components handle local layout formatting rules */
</style>